export const useWallet = () => {
  const { currentRouteName } = useRoutes();
  const { fetchUserProfile, userCurrency, userLanguage } = useUserData();
  const { parameters } = useParameters();
  const { isAuthenticated } = storeToRefs(useAuthStore());
  const { allCountriesList } = storeToRefs(useCountriesStore());
  const { getWallet, getRolloverData } = useWalletStore();

  const { realWallet, bonusWallet, cashbackWallet, isLoading } =
    storeToRefs(useWalletStore());

  const { getUserPixKey, getUserMexBankAccount } = useUserDataStore();

  function getDynamicMoneyFormat({
    value,
    currency,
    locale,
    withCents = true,
  }: {
    value: number;
    currency: string;
    locale: string;
    withCents?: boolean;
  }) {
    if (value === undefined) {
      return '$ -,--';
    }

    // Testar outras moedas
    // currency = 'NGN';

    const format_money = new Intl.NumberFormat(locale, {
      style: 'currency',
      currencyDisplay: 'narrowSymbol',
      currency,
      minimumFractionDigits: withCents ? 2 : 0,
      maximumFractionDigits: withCents ? 2 : 0,
    });

    return format_money.format(value);
  }

  function getBonusMoneyFormat(value: number) {
    if (value === undefined) {
      return 'B$ -,--';
    }

    const format_money = new Intl.NumberFormat('pt-br', {
      style: 'currency',
      currency: 'BRL',
    });

    return format_money.format(value).replace('R', 'B');
  }

  function showMoneyFormat({
    value,
    withCents = true,
  }: {
    value: number;
    withCents?: boolean;
  }) {
    let currency = userCurrency.value;

    if (!isAuthenticated.value && allCountriesList.value?.length) {
      const country = allCountriesList.value.find(
        (c: any) => c.code === parameters.value.country,
      );

      if (country) {
        currency = country.currency || '';
      }
    }

    return getDynamicMoneyFormat({
      value,
      currency,
      locale: userLanguage.value,
      withCents,
    });
  }

  const walletIsLoading = computed<boolean>(() => {
    return isLoading.value;
  });

  const walletId = computed<number>(() => {
    return realWallet.value.walletId;
  });

  const walletRealNumber = computed<number>(() => {
    return realWallet.value.credit;
  });

  const walletBonusNumber = computed<number>(() => {
    return bonusWallet.value.credit;
  });

  const walletRealFormatted = computed<string>(() => {
    return getDynamicMoneyFormat({
      value: realWallet.value.credit,
      currency: userCurrency.value,
      locale: userLanguage.value,
    });
  });

  const walletBonusFormatted = computed<string>(() => {
    return getBonusMoneyFormat(bonusWallet.value.credit);
  });

  // Símbolo da moeda separado do número (ex.: "R$" + "1.000,00") — os cards
  // V2 (UserV2CoinsCard, ver BalancesBar.vue) pintam o prefixo apagado e o
  // valor no branco cheio, então precisam dos dois em props separadas em vez
  // de walletRealFormatted/walletBonusFormatted, que devolvem tudo junto numa
  // string só. O saldo bônus aqui usa "R$" (símbolo real), não o "B$" de
  // walletBonusFormatted, que é só do card antigo.
  const walletCurrencySymbol = computed<string>(() => {
    const parts = new Intl.NumberFormat(userLanguage.value, {
      style: 'currency',
      currencyDisplay: 'narrowSymbol',
      currency: userCurrency.value,
    }).formatToParts(0);

    return parts.find((part) => part.type === 'currency')?.value ?? '';
  });

  const walletRealFormattedNumber = computed<string>(() =>
    walletRealNumber.value.toLocaleString(userLanguage.value, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  );

  const walletBonusFormattedNumber = computed<string>(() =>
    walletBonusNumber.value.toLocaleString(userLanguage.value, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  );

  const walletHasReal = computed<boolean>(() => {
    return !!realWallet.value.credit;
  });

  const walletHasBonus = computed<boolean>(() => {
    return !!bonusWallet.value.credit;
  });

  const walletHasCashback = computed<boolean>(() => {
    return !!cashbackWallet.value.credit;
  });

  const walletCashbackNumber = computed<number>(() => {
    return cashbackWallet.value.credit;
  });

  const walletCashbackFormatted = computed<string>(() => {
    return getDynamicMoneyFormat({
      value: cashbackWallet.value.credit,
      currency: userCurrency.value,
      locale: userLanguage.value,
    });
  });

  const walletCashbackExpiryDatetime = computed<string>(() => {
    return cashbackWallet.value.expires_at;
  });

  const walletHasFirstDeposit = computed<boolean>(() => {
    if (isLoading.value) {
      return false;
    }

    return !!realWallet.value.hasWalletDeposit;
  });

  function setLoadingWallet(state: boolean) {
    isLoading.value = state;
  }

  function updateWallet() {
    getWallet();

    if (
      currentRouteName.value === 'user-account' ||
      currentRouteName.value === 'user-account-protection'
    ) {
      fetchUserProfile();

      if (userCurrency.value === 'BRL') {
        getUserPixKey();
      }

      if (userCurrency.value === 'MXN') {
        getUserMexBankAccount();
      }
    }

    if (currentRouteName.value === 'user-wallet') {
      getRolloverData();
    }

    const isIFrame = (input: HTMLElement | null): input is HTMLIFrameElement =>
      input !== null && input.tagName === 'IFRAME';

    const myIframe = document.getElementById('gameIframe');

    if (isIFrame(myIframe) && myIframe.contentWindow) {
      myIframe.contentWindow.postMessage({ event: 'refreshBalance' });
      window.postMessage({ event: 'refreshBalance' });
      postMessage({ event: 'refreshBalance' });
    }
  }

  return {
    showMoneyFormat,
    walletIsLoading,
    walletId,
    walletRealNumber,
    walletBonusNumber,
    walletRealFormatted,
    walletBonusFormatted,
    walletCurrencySymbol,
    walletRealFormattedNumber,
    walletBonusFormattedNumber,
    walletHasReal,
    walletHasBonus,
    walletHasFirstDeposit,
    walletHasCashback,
    walletCashbackNumber,
    walletCashbackFormatted,
    walletCashbackExpiryDatetime,
    setLoadingWallet,
    updateWallet,
  };
};
